import React, { useCallback, useRef, useState } from "react"
import PropTypes from "prop-types"
import PubSub from "pubsub.js"
import { createInvoiceValidation } from "../../../../services/api/CreateInvoiceServices"
import { Form, Input, Modal, ModalBody, ModalFooter, ModalHeader, Label } from "reactstrap"
import { saveAs } from "file-saver"
import { Button, Tooltip, Spin } from "antd"
import {
    LoadingOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
} from "@ant-design/icons"
import { flushSync } from "react-dom"

// Config
const MAX_SIZE = 5 * 1024 * 1024 // 5MB
const DEFAULT_SLOTS = 2
const MAX_SLOTS = 20
// precompiled allowed regex (simple, anchored)
const ALLOWED_REGEX = /^[A-Za-z0-9\-_. ]*$/

function CreatedInvoiceValidateModal({ isOpen, onToggleModal, listShippingCarrier = [] }) {
    // ---- utilities ----
    // Use Unicode normalization to strip diacritics, with fallback map for older environments
    const removeAscent = (str = "") => {
        if (!str) return ""
        // Prefer standard Unicode normalization (NFD) and strip combining diacritical marks
        try {
            // Use a safe regex only for the combining diacritic block (linear runtime)
            return str
                .normalize && str.normalize("NFD")
                ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D")
                : fallbackRemoveAscent(str)
        } catch (err) {
            return fallbackRemoveAscent(str)
        }
    }

    // fallback in case normalize not supported
    const fallbackRemoveAscent = (str = "") => {
        const AccentsMap = [
            "aàảãáạăằẳẵắặâầẩẫấậ",
            "AÀẢÃÁẠĂẰẲẴẮẶÂẦẨẪẤẬ",
            "dđ",
            "DĐ",
            "eèẻẽéẹêềểễếệ",
            "EÈẺẼÉẸÊỀỂỄẾỆ",
            "iìỉĩíị",
            "IÌỈĨÍỊ",
            "oòỏõóọôồổỗốộơờởỡớợ",
            "OÒỎÕÓỌÔỒỔỖỐỘƠỜỞỠỚỢ",
            "uùủũúụưừửữứự",
            "UÙỦŨÚỤƯỪỬỮỨỰ",
            "yỳỷỹýỵ",
            "YỲỶỸÝỴ",
            " $",
        ]
        let out = str
        for (let i = 0; i < AccentsMap.length; i++) {
            const set = AccentsMap[i]
            const char = set[0]
            const accents = set.substr(1)
            // replace each accent character individually (no dynamic regex from external input)
            for (let j = 0; j < accents.length; j++) {
                const a = accents[j]
                out = out.split(a).join(char)
            }
        }
        return out
    }

    // Sanitize without regex to avoid ReDoS concerns: keep only A-Z a-z 0-9 - _ . and space
    const sanitizeToAllowed = (raw = "") => {
        const noAccent = removeAscent(raw)
        let out = ""
        for (let i = 0; i < noAccent.length; i++) {
            const c = noAccent[i]
            const code = c.charCodeAt(0)
            // 0-9, A-Z, a-z
            if ((code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
                out += c
                continue
            }
            // allowed punctuation: - _ . and space
            if (c === "-" || c === "_" || c === "." || c === " ") {
                out += c
            }
            // else skip character
        }
        return out
    }

    const isAllAllowedAscii = (str = "") => ALLOWED_REGEX.test(str)

    // keep a persistent counter across renders
    const localIdCounterRef = useRef(0)

    const makeSecureId = () => {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return `${Date.now()}_${crypto.randomUUID()}`
        }

        if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
            const arr = new Uint8Array(8) // 64 bits
            crypto.getRandomValues(arr)
            const hex = Array.from(arr).map(b => b.toString(16).padStart(2, "0")).join("")
            return `${Date.now()}_${hex}`
        }

        try {
            const { randomBytes } = require && typeof require === "function" ? require("crypto") : {}
            if (typeof randomBytes === "function") {
                return `${Date.now()}_${randomBytes(8).toString("hex")}`
            }
        } catch (err) {
            // ignore and fallback to deterministic unique id
        }

        const perfNow = (typeof performance !== "undefined" && typeof performance.now === "function")
            ? Math.floor(performance.now())
            : 0
        const counter = (localIdCounterRef.current = (localIdCounterRef.current + 1) & 0xffffffff)
        return `${Date.now()}_${perfNow}_${counter}`
    }

    const makeSlot = (overrides = {}) => ({
        id: makeSecureId(),
        file: null,
        filename: "",
        shippingCarrier: "multrans_us",
        validate: { file: "", filename: "" },
        loading: false,
        error: "",
        success: false,
        successAt: "",
        ...overrides,
    })

    // ---- state ----
    const [slots, setSlots] = useState(() => Array.from({ length: DEFAULT_SLOTS }, () => makeSlot()))
    const [note, setNote] = useState("")
    const [globalLoading, setGlobalLoading] = useState(false)

    // refs for uncontrolled inputs
    const inputRefs = useRef({})

    const bulkInputRef = useRef(null)
    const listRef = useRef(null)

    // ---- validations ----
    const validateSlot = useCallback((slot) => {
        const v = { file: "", filename: "" }
        if (!slot.file) v.file = "Vui lòng chọn file CSV."
        if (!slot.filename || slot.filename.trim() === "") v.filename = "Vui lòng nhập tên file."
        else if (!isAllAllowedAscii(slot.filename)) v.filename = "Tên file chỉ được chứa chữ A-Z, số, - _ . và khoảng trắng."
        return v
    }, [])

    // ---- actions ----
    const addSlot = useCallback(() => {
        setSlots((s) => {
            if (s.length >= MAX_SLOTS) return s
            return [...s, makeSlot()]
        })
    }, [])

    const removeSlot = useCallback((index) => {
        // preserve scrollTop
        const el = listRef.current
        const prevScroll = el ? el.scrollTop : 0

        flushSync(() => {
            setSlots((prev) => {
                const copy = prev.slice()
                const target = copy[index]
                if (target && (target.loading || target.success)) return prev
                const removed = copy.splice(index, 1)
                if (removed && removed[0] && inputRefs.current[removed[0].id]) {
                    delete inputRefs.current[removed[0].id]
                }
                return copy.length ? copy : [makeSlot()]
            })
        })

        requestAnimationFrame(() => {
            if (!el) return
            const max = el.scrollHeight - el.clientHeight
            el.scrollTop = Math.max(0, Math.min(prevScroll || 0, max))
        })
    }, [])

    const bulkAddFiles = useCallback((e) => {
        const files = e.target.files
        if (!files || !files.length) {
            if (e.target) e.target.value = null
            return
        }

        flushSync(() => { })

        setTimeout(() => {
            setSlots((prev) => {
                const copy = prev.slice()
                const emptyIndices = []
                for (let i = 0; i < copy.length; i++) {
                    if (!copy[i].file && !copy[i].loading && !copy[i].success) emptyIndices.push(i)
                }

                for (let fileIndex = 0; fileIndex < files.length && (emptyIndices.length > 0 || copy.length < MAX_SLOTS); fileIndex++) {
                    const f = files[fileIndex]
                    const dotIdx = f.name.lastIndexOf(".")
                    const rawName = dotIdx > 0 ? f.name.substring(0, dotIdx) : f.name
                    const ext = dotIdx > -1 ? f.name.substring(dotIdx + 1).toLowerCase() : ""

                    const slotData = { ...makeSlot() }

                    if (f.size > MAX_SIZE) {
                        slotData.file = null
                        slotData.filename = sanitizeToAllowed(rawName)
                        slotData.validate.file = "File giới hạn kích thước 5MB. Vui lòng kiểm tra lại."
                    } else if (ext !== "csv") {
                        slotData.file = null
                        slotData.filename = sanitizeToAllowed(rawName)
                        slotData.validate.file = "File upload định dạng CSV. Vui lòng kiểm tra lại."
                    } else {
                        slotData.file = f
                        slotData.filename = sanitizeToAllowed(rawName)
                        slotData.validate.filename = isAllAllowedAscii(slotData.filename) ? "" : "Tên file chỉ được chứa chữ A-Z, số, - _ . và khoảng trắng."
                        slotData.validate.file = ""
                    }

                    if (emptyIndices.length > 0) {
                        const idx = emptyIndices.shift()
                        copy[idx] = { ...copy[idx], ...slotData }
                    } else {
                        copy.push(slotData)
                    }

                    if (copy.length >= MAX_SLOTS) break
                }

                return copy
            })

            if (bulkInputRef.current) bulkInputRef.current.value = null

            setTimeout(() => {
                const curr = inputRefs.current
                setSlots((prev) => {
                    for (const s of prev) {
                        const ref = curr[s.id]
                        if (ref) ref.value = s.filename || ""
                    }
                    return prev
                })
            }, 0)
        }, 0)
    }, [])

    const onChangeFile = useCallback((index) => (e) => {
        const f = e.target.files && e.target.files[0]
        if (!f) return

        setSlots((prev) => {
            const next = prev.slice()
            const currentSlot = next[index]
            if (!currentSlot) return prev
            // don't allow replacing if loading or success
            if (currentSlot.loading || currentSlot.success) return prev

            // safer extraction without regex
            const dotIdx = f.name.lastIndexOf(".")
            const rawName = dotIdx > 0 ? f.name.substring(0, dotIdx) : f.name
            const ext = dotIdx > -1 ? f.name.substring(dotIdx + 1).toLowerCase() : ""

            const slot = { ...currentSlot }

            if (f.size > MAX_SIZE) {
                slot.file = null
                slot.filename = sanitizeToAllowed(rawName)
                slot.validate.file = "File giới hạn kích thước 5MB. Vui lòng kiểm tra lại."
            } else if (ext !== "csv") {
                slot.file = null
                slot.filename = sanitizeToAllowed(rawName)
                slot.validate.file = "File upload định dạng CSV. Vui lòng kiểm tra lại."
            } else {
                slot.file = f
                slot.filename = sanitizeToAllowed(rawName)
                slot.validate.file = ""
                slot.validate.filename = isAllAllowedAscii(slot.filename) ? "" : "Tên file chỉ được chứa chữ A-Z, số, - _ . và khoảng trắng."
                // reset previous error/success if choosing new file
                slot.error = ""
                slot.success = false
                slot.successAt = ""
            }
            next[index] = slot

            // set uncontrolled input value next tick
            setTimeout(() => {
                const ref = inputRefs.current[slot.id]
                if (ref) ref.value = slot.filename || ""
            }, 0)

            return next
        })
    }, [])

    // ---- BLOCK Vietnamese: beforeinput + fallback onInput + paste ----

    // beforeInput: try to prevent insertion of any non-ASCII (non-allowed) characters
    const handleBeforeInput = useCallback((slotId) => (e) => {
        // e.nativeEvent.data contains the text to be inserted (may be null for some inputTypes)
        const data = e.nativeEvent && e.nativeEvent.data
        if (!data) return
        // if any char in data is outside ASCII allowed set -> prevent
        if (!isAllAllowedAscii(data)) {
            e.preventDefault()
            // optionally flash or show tooltip; for simplicity we do nothing UI-wise
        }
    }, [])

    // fallback onInput: sanitize any leftover characters (for older browsers)
    const handleInputFallback = useCallback((slotId) => (e) => {
        const el = e.target
        const before = el.value
        const sanitized = sanitizeToAllowed(before)
        if (sanitized !== before) {
            const sel = el.selectionStart || 0
            // compute sanitized prefix length to place caret
            const prefix = before.slice(0, sel)
            const sanitizedPrefixLen = sanitizeToAllowed(prefix).length
            el.value = sanitized
            try {
                el.setSelectionRange(sanitizedPrefixLen, sanitizedPrefixLen)
            } catch (err) { /* ignore */ }
        }
    }, [])

    // onPaste: intercept and paste sanitized text only
    const handlePaste = useCallback((slotId) => (e) => {
        e.preventDefault()
        const text = (e.clipboardData || window.clipboardData).getData("text") || ""
        const sanitized = sanitizeToAllowed(text)
        const el = e.target
        const start = el.selectionStart || 0
        const end = el.selectionEnd || 0
        const before = el.value.slice(0, start)
        const after = el.value.slice(end)
        const newVal = sanitizeToAllowed(before + sanitized + after)
        el.value = newVal
        const newPos = sanitizeToAllowed(before + sanitized).length
        try {
            el.setSelectionRange(newPos, newPos)
        } catch (err) { /* ignore */ }
    }, [])

    // onBlur: sync uncontrolled input into state (sanitized)
    const onBlurFilename = useCallback((slotId, index) => (e) => {
        const el = inputRefs.current[slotId]
        const raw = (el && el.value) || ""
        const sanitized = sanitizeToAllowed(raw)
        setSlots((prev) => {
            const next = prev.slice()
            const slot = { ...next[index] }
            if (slot.filename !== sanitized) slot.filename = sanitized
            slot.validate.filename = !sanitized || sanitized.trim() === "" ? "Vui lòng nhập tên file." : isAllAllowedAscii(sanitized) ? "" : "Tên file chỉ được chứa chữ A-Z, số, - _ . và khoảng trắng."
            next[index] = slot
            return next
        })
        if (el && el.value !== sanitized) el.value = sanitized
    }, [])

    const onChangeCarrier = useCallback((index) => (e) => {
        const val = e.target.value

        // preserve scrollTop
        const el = listRef.current
        const prevScroll = el ? el.scrollTop : 0

        flushSync(() => {
            setSlots((prev) => {
                const next = prev.slice()
                const current = next[index]
                if (!current || current.loading || current.success) return prev
                next[index] = { ...next[index], shippingCarrier: val }
                return next
            })
        })

        requestAnimationFrame(() => {
            if (!el) return
            const max = el.scrollHeight - el.clientHeight
            el.scrollTop = Math.max(0, Math.min(prevScroll || 0, max))
        })
    }, [])

    const downloadCSVTemplate = useCallback(() => {
        const csvHeader = [
            "No",
            "Reference number",
            "Tracking number",
            "Gross weight - Actual (kg)",
            "Gross weight - Book (kg)",
            "L - Actual (cm)",
            "W - Actual (cm)",
            "H - Actual (cm)",
            "L - Book (cm)",
            "W - Book (cm)",
            "H - Book (cm)",
            "Oversize",
            "Volume weight (kg)",
            "Charge weight (kg)",
            "Cost",
        ]
        const csvRows = [["", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]]
        const csvContent = "\uFEFF" + [csvHeader.join(","), ...csvRows.map((r) => r.join(","))].join("\n")
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" })
        saveAs(blob, "template-import-invoice-validation.csv")
    }, [])

    // ---- helper to format timestamp for successAt ----
    const formatNowForSuccess = () => {
        try {
            return new Date().toLocaleString("vi-VN", { timeZone: "Asia/Bangkok" })
        } catch (err) {
            return new Date().toLocaleString()
        }
    }

    // ---- retry single slot upload ----
    const retrySlot = useCallback(async (index) => {
        // set only that slot to loading
        setSlots((prev) => {
            const next = prev.slice()
            const s = next[index]
            if (!s || !s.file) return prev
            // ignore if already loading or success
            if (s.loading || s.success) return prev
            next[index] = { ...s, loading: true, error: "" }
            return next
        })

        // read latest slot state (from closure slots; slots is a dependency so should be recent)
        const slotSnapshot = slots[index]
        if (!slotSnapshot || !slotSnapshot.file) {
            setSlots((prev) => {
                const next = prev.slice()
                if (next[index]) next[index] = { ...next[index], loading: false, error: "Không có file để upload" }
                return next
            })
            return
        }

        const formData = new FormData()
        formData.append("invoice", slotSnapshot.file)
        formData.append("note", note)
        const sanitizedFilename = sanitizeToAllowed(slotSnapshot.filename || "").trim()
        formData.append("filename", sanitizedFilename)
        formData.append("shipping_carrier", slotSnapshot.shippingCarrier)

        try {
            const { success, message } = await createInvoiceValidation(formData)
            if (!success) {
                setSlots((prev) => {
                    const next = prev.slice()
                    if (next[index]) next[index] = { ...next[index], loading: false, error: message || "Upload thất bại" }
                    return next
                })
            } else {
                const successAt = formatNowForSuccess()
                setSlots((prev) => {
                    const next = prev.slice()
                    if (next[index]) next[index] = { ...next[index], loading: false, error: "", success: true, successAt }
                    return next
                })
                PubSub.publish("LOAD_LIST_INVOICE")
            }
        } catch (err) {
            setSlots((prev) => {
                const next = prev.slice()
                if (next[index]) next[index] = { ...next[index], loading: false, error: (err && err.message) || "Lỗi hệ thống khi upload" }
                return next
            })
        }
    }, [slots, note])

    // ---- submit: sequential upload using snapshot ----
    const onSubmit = useCallback(async () => {
        // flush uncontrolled inputs into state
        setSlots((prev) => {
            const next = prev.slice()
            for (let i = 0; i < next.length; i++) {
                const s = next[i]
                const ref = inputRefs.current[s.id]
                if (ref) {
                    const sanitized = sanitizeToAllowed(ref.value || "")
                    s.filename = sanitized
                    s.validate.filename = !sanitized || sanitized.trim() === "" ? "Vui lòng nhập tên file." : isAllAllowedAscii(sanitized) ? "" : "Tên file chỉ được chứa chữ A-Z, số, - _ . và khoảng trắng."
                } else {
                    s.validate.filename = !s.filename || s.filename.trim() === "" ? "Vui lòng nhập tên file." : isAllAllowedAscii(s.filename) ? "" : "Tên file chỉ được chứa chữ A-Z, số, - _ . và khoảng trắng."
                }
            }
            return next
        })

        // snapshot of ready slots
        const snapshot = []
        const currentSlots = slots
        for (let i = 0; i < currentSlots.length; i++) {
            const s = currentSlots[i]
            const v = validateSlot(s)
            // only include slots that have a file, pass validation, and are NOT currently loading or already success
            if (s.file && !v.file && !v.filename && !s.loading && !s.success) {
                snapshot.push({ index: i, slot: { ...s } })
            }
        }

        if (snapshot.length === 0) return

        setGlobalLoading(true)

        // Set loading=true for all snapshot indices at once (so UI shows all as loading immediately)
        const indicesToLoad = snapshot.map((it) => it.index)
        setSlots((prev) => {
            const next = prev.slice()
            for (const idx of indicesToLoad) {
                if (next[idx] && !next[idx].loading && !next[idx].success) {
                    next[idx] = { ...next[idx], loading: true, error: "" }
                }
            }
            return next
        })

        let allSuccess = true

        for (const item of snapshot) {
            const { index, slot } = item

            const formData = new FormData()
            formData.append("invoice", slot.file)
            formData.append("note", note)
            const sanitizedFilename = sanitizeToAllowed(slot.filename || "").trim()
            formData.append("filename", sanitizedFilename)
            formData.append("shipping_carrier", slot.shippingCarrier)

            try {
                const { success, message } = await createInvoiceValidation(formData)
                if (!success) {
                    allSuccess = false
                    setSlots((prev) => {
                        const next = prev.slice()
                        next[index] = { ...next[index], loading: false, error: message || "Upload thất bại" }
                        return next
                    })
                } else {
                    const successAt = formatNowForSuccess()
                    setSlots((prev) => {
                        const next = prev.slice()
                        next[index] = { ...next[index], loading: false, error: "", success: true, successAt }
                        return next
                    })
                }
            } catch (err) {
                allSuccess = false
                setSlots((prev) => {
                    const next = prev.slice()
                    next[index] = { ...next[index], loading: false, error: (err && err.message) || "Lỗi hệ thống khi upload" }
                    return next
                })
            }
        }

        setGlobalLoading(false)
        if (allSuccess) {
            if (typeof onToggleModal === "function") onToggleModal()
            PubSub.publish("LOAD_LIST_INVOICE")
            setSlots(Array.from({ length: DEFAULT_SLOTS }, () => makeSlot()))
            setNote("")
            inputRefs.current = {}
        }
    }, [slots, note, onToggleModal, validateSlot])

    // ---- helpers for UI state ----
    const isAnySlotLoading = globalLoading || slots.some((s) => s.loading)
    // has at least one ready: must be a file, pass validations, and NOT loading/success (so we won't re-upload locked ones)
    const hasAtLeastOneReady = slots.some((s) => s.file && !s.validate.file && !s.validate.filename && !s.loading && !s.success)
    const disableUploadBtn = isAnySlotLoading || !hasAtLeastOneReady

    // close handler that reloads page if any success ----
    const handleClose = useCallback(() => {
        const anySuccess = slots.some((s) => s.success)
        if (typeof onToggleModal === "function") onToggleModal()
        if (anySuccess) {
            PubSub.publish("LOAD_LIST_INVOICE")
            setSlots(Array.from({ length: DEFAULT_SLOTS }, () => makeSlot()))
            setNote("")
            inputRefs.current = {}
        }
    }, [slots, onToggleModal])

    // ---- SlotItem ----
    const StatusIcon = ({ slot }) => {
        if (slot.loading) return <LoadingOutlined style={{ color: "#1890ff", fontSize: 16 }} spin />
        if (slot.success) return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
        if (slot.error) return <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
        return <></>
    }

    const getBorderColorForSlot = (slot) => {
        if (slot.loading) return "#1890ff" // blue
        if (slot.success) return "#52c41a" // green
        if (slot.error) return "#ff4d4f" // red
        return "#d9d9d9" // gray
    }

    const SlotItem = ({ slot, index }) => {
        const locked = slot.loading || slot.success // locked state: cannot edit/remove

        // prepare tooltip content
        let tooltipTitle = ""
        if (slot.loading) {
            tooltipTitle = `Uploading...${slot.filename ? `\n${slot.filename}` : ""}`
        } else if (slot.success) {
            tooltipTitle = `Uploaded thành công${slot.successAt ? ` — ${slot.successAt}` : ""}${slot.filename ? `\n${slot.filename}` : ""}`
        } else if (slot.error) {
            tooltipTitle = `Lỗi: ${slot.error}${slot.filename ? `\n${slot.filename}` : ""}`
        } else {
            tooltipTitle = slot.file ? `Sẵn sàng: ${slot.filename || slot.file.name}` : "No file"
        }

        const borderColor = getBorderColorForSlot(slot)

        return (
            <div
                key={slot.id}
                className="card mb-2 p-3"
                style={{
                    border: `2px solid ${borderColor}`,
                    transition: "border-color 0.15s, box-shadow 0.15s",
                }}
            >
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                        <strong>File #{index + 1}</strong>
                        <small className="text-muted ml-2">— {slot.file ? slot.file.name : "No file"}</small>

                        {/* status icon + tooltip (moved next to title) */}
                        {/* <Tooltip title={<pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{tooltipTitle}</pre>}> */}
                        <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 12 }}>
                            <StatusIcon slot={slot} />
                            <span style={{ marginLeft: 8, fontWeight: 'bold' }}>
                                {slot.loading ? (
                                    <span>
                                        <Spin size="small" /> <span className="text-primary">Uploading...</span>
                                    </span>
                                ) : slot.success ? (
                                    <span className="text-success">Uploaded</span>
                                ) : slot.error ? (
                                    <span className="text-danger">Error</span>
                                ) : (
                                    <small className="text-muted"></small>
                                )}
                            </span>
                        </span>
                        {/* </Tooltip> */}

                        {/* if success show timestamp */}
                        {slot.success && slot.successAt && (
                            <small className="text-success ml-2" style={{ marginLeft: 8 }}>
                                {slot.successAt}
                            </small>
                        )}
                    </div>

                    <div className="d-flex align-items-center">
                        {/* file input disabled when locked */}
                        {!slot.success &&
                            <>
                                <input
                                    type="file"
                                    accept=".csv"
                                    id={`slot-file-${slot.id}`}
                                    style={{ display: "none" }}
                                    onChange={onChangeFile(index)}
                                    disabled={locked}
                                />
                                <label
                                    htmlFor={`slot-file-${slot.id}`}
                                    className={`btn btn-sm btn-outline-primary mr-2 ${locked ? "disabled" : ""}`}
                                    style={{ marginBottom: 0, pointerEvents: locked ? "none" : "auto" }}
                                >
                                    Chọn file
                                </label>
                            </>
                        }

                        {/* Retry button when error */}
                        {slot.error &&
                            <Tooltip title="Retry upload file này">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning mr-2 d-flex align-items-center"
                                    onClick={() => retrySlot(index)}
                                    disabled={slot.loading}
                                >
                                    <ReloadOutlined style={{ marginRight: 6 }} />
                                    Retry
                                </button>
                            </Tooltip>
                        }
                        {!slot.success &&
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeSlot(index)}
                                disabled={locked}
                            >
                                Remove
                            </button>
                        }
                    </div>
                </div>
                {slot.validate.file && <small className="text-danger mt-1">{slot.validate.file}</small>}
                <div className="form-row">
                    <div className="form-group col-md-4">
                        <Label for={`filename-${slot.id}`}>File name</Label>
                        <input
                            id={`filename-${slot.id}`}
                            type="text"
                            className="form-control"
                            placeholder="File name"
                            defaultValue={slot.filename}
                            ref={(el) => {
                                if (el) inputRefs.current[slot.id] = el
                                else if (inputRefs.current[slot.id]) delete inputRefs.current[slot.id]
                            }}
                            onBeforeInput={handleBeforeInput(slot.id)}
                            onInput={handleInputFallback(slot.id)}
                            onPaste={handlePaste(slot.id)}
                            onBlur={onBlurFilename(slot.id, index)}
                            inputMode="latin"
                            lang="en"
                            autoCapitalize="off"
                            spellCheck={false}
                            maxLength={200}
                            readOnly={locked} // prevent editing when locked
                            disabled={locked}
                        />
                        {slot.validate.filename && <div className="text-danger mt-1">{slot.validate.filename}</div>}
                    </div>

                    <div className="form-group col-md-4">
                        <Label>Shipping carrier</Label>
                        <Input
                            type="select"
                            className="form-control"
                            value={slot.shippingCarrier}
                            onChange={onChangeCarrier(index)}
                            disabled={locked} // cannot change when locked
                        >
                            {listShippingCarrier.map((c, idx) => (
                                <option key={idx} value={c.value}>
                                    {c.name}
                                </option>
                            ))}
                        </Input>
                    </div>

                    <div className="form-group col-md-4 d-flex flex-column justify-content-end">
                        {/* intentionally left blank (status shown above) */}
                    </div>
                </div>
                {slot.error && <small className="text-danger mt-1">{slot.error}</small>}
            </div>
        )
    }

    // ---- render ----
    return (
        <Modal isOpen={isOpen} className="CreateInvoiceModal" size="lg" toggle={handleClose}>
            <ModalHeader toggle={handleClose}>Create invoice validation — Multiple files</ModalHeader>
            <ModalBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <Button onClick={downloadCSVTemplate} className="btn btn-link p-0 text-decoration-none">
                            Download template
                        </Button>

                        <input
                            ref={bulkInputRef}
                            id="bulk-files"
                            type="file"
                            accept=".csv"
                            multiple
                            onChange={bulkAddFiles}
                            style={{ display: "none" }}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary mb-0"
                            style={{ marginLeft: 8 }}
                            onClick={() => {
                                if (bulkInputRef.current) {
                                    bulkInputRef.current.value = null
                                    bulkInputRef.current.click()
                                }
                            }}
                        >
                            Bulk add files
                        </button>
                    </div>

                    <div>
                        <Button type="primary" className="btn btn-sm btn-primary" onClick={addSlot} disabled={slots.length >= MAX_SLOTS}>
                            + Add slot
                        </Button>
                        <small className="text-muted ml-2">({slots.length}/{MAX_SLOTS})</small>
                    </div>
                </div>

                <Form>
                    <div className="listFileUpload mb-3" ref={listRef}>
                        {slots.map((slot, idx) => (
                            <SlotItem key={slot.id} slot={slot} index={idx} />
                        ))}
                    </div>

                    <div className="form-group">
                        <Label>Note (shared)</Label>
                        <textarea className="form-control" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                </Form>
            </ModalBody>

            <ModalFooter>
                <div className="ModalButtons d-flex">
                    <button className="btn btn-light border mr-2" onClick={handleClose} disabled={isAnySlotLoading}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={onSubmit} disabled={disableUploadBtn}>
                        {globalLoading ? "Processing..." : "Upload all"}
                    </button>
                </div>
            </ModalFooter>
        </Modal>
    )
}

CreatedInvoiceValidateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onToggleModal: PropTypes.func.isRequired,
    listShippingCarrier: PropTypes.array,
}

export default CreatedInvoiceValidateModal
